import { useRef } from 'react';
import { getDefaultLogoUrls, getPlatformDefaultBranding, isSaasPlatform } from '../utils/companyBranding';
import { resolveSaasLogoUrl } from '../saas/lib/saasLogo';
import { SAAS_DEFAULT_LOADER_STYLE } from '../saas/lib/loadingStyle';
import { isSaasMarketingContentPath } from '../saas/components/SaasMarketingBootShell';
import SaasBrandMark from '../saas/components/SaasBrandMark';
import { SaasPulseLoader } from '../saas/components/SaasPulseLoader';
import { useTheme } from '../saas/contexts/ThemeContext';
import { useTenantBrand } from '../contexts/TenantBrandContext';

interface BubblesLoaderProps {
  /** Smaller bubble field for modals and in-dashboard placeholders */
  compact?: boolean;
  className?: string;
}

/** Floating soap-bubble background animation (T&B site + optional SaaS org setting). */
export function BubblesLoader({ compact = false, className = '' }: BubblesLoaderProps) {
  const bubblesRef = useRef(
    Array.from({ length: compact ? 10 : 14 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      size: (compact ? 14 : 20) + Math.random() * (compact ? 22 : 40),
      duration: 3 + Math.random() * 4,
      delay: Math.random() * 2,
    })),
  );

  return (
    <div className={`absolute inset-0 pointer-events-none overflow-hidden ${className}`}>
      {bubblesRef.current.map((bubble) => (
        <div
          key={bubble.id}
          className="absolute rounded-full bg-blue-200/40 border-2 border-blue-300/50 animate-bubble-float"
          style={{
            left: `${bubble.left}%`,
            bottom: '-50px',
            width: `${bubble.size}px`,
            height: `${bubble.size}px`,
            animationDuration: `${bubble.duration}s`,
            animationDelay: `${bubble.delay}s`,
          }}
        />
      ))}
    </div>
  );
}

interface BrandedLoadingScreenProps {
  /** Optional caption — omit for auth/dashboard gates (no "Loading your dashboard" copy). */
  message?: string;
  compact?: boolean;
  /**
   * SaaS full-screen gates (login, route lazy-load, workspace boot): ServiceApp logo only,
   * not the signed-in tenant org name/logo. Defaults to true when `compact` is false.
   */
  platformOnly?: boolean;
}

/** Branded loader — tenant / ServiceApp logo on SaaS; Tools & Bubbles on tb-site. */
export default function BrandedLoadingScreen({
  message,
  compact = false,
  platformOnly: platformOnlyProp,
}: BrandedLoadingScreenProps) {
  const { branding } = useTenantBrand();
  const { theme, loaderAppearance } = useTheme();
  const saas = isSaasPlatform();
  const marketingDayPath =
    typeof window !== 'undefined' && isSaasMarketingContentPath(window.location.pathname);
  const defaults = getPlatformDefaultBranding();
  const platformOnly = platformOnlyProp ?? (saas && !compact);
  const brandSource = platformOnly ? defaults : branding;
  const logoSrc = saas
    ? resolveSaasLogoUrl(platformOnly ? defaults.logoUrl : branding.logoUrl || defaults.logoUrl)
    : branding.logoUrl || defaults.logoUrl || getDefaultLogoUrls().png;

  const loaderStyle = platformOnly
    ? SAAS_DEFAULT_LOADER_STYLE
    : (branding.loaderStyle ?? SAAS_DEFAULT_LOADER_STYLE);
  const useBubbles = !saas || loaderStyle === 'bubbles';
  const usePulse = saas && loaderStyle === 'pulse';
  const useLightBg =
    !saas ||
    loaderStyle === 'bubbles' ||
    marketingDayPath ||
    loaderAppearance !== 'match_theme' ||
    theme === 'light';

  if (saas) {
    return (
      <div
        className={`relative overflow-hidden w-full min-w-full flex items-center justify-center ${
          compact
            ? useLightBg
              ? 'min-h-[260px] rounded-2xl bg-[radial-gradient(ellipse_at_50%_40%,#eef2ff_0%,#ffffff_52%,#ecfeff_100%)]'
              : 'min-h-[260px] rounded-2xl bg-[var(--saas-canvas,#161d2e)]'
            : useLightBg
              ? 'min-h-screen min-h-[100dvh] bg-[radial-gradient(ellipse_at_50%_38%,#eef2ff_0%,#ffffff_48%,#f0fdfa_100%)]'
              : 'min-h-screen min-h-[100dvh] bg-[var(--saas-canvas,#161d2e)]'
        }`}
        aria-busy="true"
        aria-label={message?.trim() || 'Loading'}
      >
        {useBubbles && <BubblesLoader compact={compact} />}
        {usePulse && <SaasPulseLoader compact={compact} tone={useLightBg ? 'bright' : 'night'} />}

        <div className="relative z-10 flex flex-col items-center px-4 text-center">
          <SaasBrandMark
            src={logoSrc}
            alt={platformOnly ? 'ServiceApp' : `${brandSource.companyName} logo`}
            size={compact ? 'md' : 'lg'}
            className={
              useLightBg
                ? 'shadow-[0_18px_40px_-18px_rgba(79,70,229,0.45)] ring-indigo-100/80'
                : 'shadow-[0_18px_40px_-18px_rgba(0,0,0,0.45)]'
            }
          />
          {message?.trim() ? (
            <p
              className={
                useLightBg
                  ? 'mt-5 text-sm font-medium tracking-wide text-slate-500'
                  : 'mt-5 text-sm font-medium tracking-wide text-slate-300'
              }
            >
              {message}
            </p>
          ) : null}
          <div
            className={`mt-5 h-1 overflow-hidden rounded-full ${
              compact ? 'w-20' : 'w-28'
            } ${useLightBg ? 'bg-indigo-100' : 'bg-white/10'}`}
          >
            <div
              className={`h-full w-1/2 rounded-full animate-saas-loader-bar ${
                useLightBg
                  ? 'bg-gradient-to-r from-indigo-500 via-violet-500 to-cyan-400'
                  : 'bg-gradient-to-r from-indigo-400 via-violet-400 to-cyan-300'
              }`}
            />
          </div>
        </div>
      </div>
    );
  }

  const logoSize = compact ? 'w-20 h-20' : 'w-24 h-24';
  const imgSize = compact ? 'w-14 h-14' : 'w-16 h-16';

  return (
    <div
      className={`relative overflow-hidden w-full min-w-full ${
        compact
          ? 'min-h-[260px] rounded-2xl bg-gradient-to-br from-blue-50 via-white to-cyan-50'
          : 'min-h-screen min-h-[100dvh] bg-gradient-to-br from-blue-50 via-white to-cyan-50'
      } flex items-center justify-center`}
      aria-busy="true"
      aria-label={message?.trim() || 'Loading'}
    >
      <BubblesLoader compact={compact} />

      <div className="text-center relative z-10 px-4">
        <div className="relative mb-5 flex items-center justify-center">
          <div className="relative">
            <div
              className={`${logoSize} rounded-full bg-white border-4 border-white shadow-lg flex items-center justify-center overflow-hidden`}
            >
              <img
                src={logoSrc}
                alt={`${brandSource.companyName} logo`}
                className={`${imgSize} object-contain`}
                decoding="async"
                onError={(e) => {
                  const img = e.currentTarget;
                  const fallbacks = getDefaultLogoUrls();
                  if (!img.src.endsWith(fallbacks.png)) {
                    img.src = fallbacks.png;
                  }
                }}
              />
            </div>
            <div
              className="absolute -top-2 -right-2 w-12 h-12 rounded-full bg-blue-300/70 border-2 border-white animate-pulse"
              style={{ animationDelay: '0.3s' }}
            />
            <div
              className="absolute -bottom-2 -left-2 w-10 h-10 rounded-full bg-cyan-300/70 border-2 border-white animate-pulse"
              style={{ animationDelay: '0.6s' }}
            />
          </div>
        </div>
        {message?.trim() ? <p className="text-gray-700 font-semibold tracking-wide">{message}</p> : null}
      </div>
    </div>
  );
}
